import type { Block, Document, Section, ViewMode } from '../types';
import { people } from '../data';
import { Bulb, Chart, Clock, Grip, Pencil, Plus, Sparkle, Trash } from './DocIcons';

// ── Block renderers ────────────────────────────────────────────────────────

function BlockP({ text }: { text: string }) {
  return <p style={{ margin: '0 0 12px', fontSize: 15, lineHeight: 1.62, color: 'var(--ink-2)', textWrap: 'pretty' } as React.CSSProperties}>{text}</p>;
}

function BlockUL({ items }: { items: string[] }) {
  return (
    <ul style={{ margin: '0 0 12px', paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: 'flex', gap: 10, fontSize: 14.5, lineHeight: 1.55, color: 'var(--ink-2)' }}>
          <span style={{ width: 5, height: 5, borderRadius: 3, background: 'var(--blue)', marginTop: 9, flexShrink: 0 }} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function BlockKV({ rows }: { rows: [string, string][] }) {
  return (
    <div style={{ margin: '0 0 12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', borderTop: i ? '1px solid var(--line-2)' : 'none' }}>
          <div style={{ width: '38%', padding: '9px 14px', fontWeight: 600, fontSize: 13.5, color: 'var(--ink)', background: 'var(--surface-2)', borderRight: '1px solid var(--line-2)' }}>{r[0]}</div>
          <div style={{ flex: 1, padding: '9px 14px', fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>{r[1]}</div>
        </div>
      ))}
    </div>
  );
}

function DocBlock({ block }: { block: Block }) {
  if (block.t === 'p') return <BlockP text={block.text ?? ''} />;
  if (block.t === 'ul') return <BlockUL items={block.items ?? []} />;
  if (block.t === 'kv') return <BlockKV rows={block.rows ?? []} />;
  return null;
}

// ── Skeleton / generating state ────────────────────────────────────────────

function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '6px 0 4px', animation: 'doc-fade-up 0.2s ease' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ai-ink)', fontSize: 12.5, fontWeight: 600, marginBottom: 2 }}>
        <Sparkle size={14} className="doc-spin" /> Vibelin is drafting this section…
      </div>
      {[92, 100, 78, 96, 64].map((w, i) => (
        <div key={i} className="doc-skeleton" style={{ width: w + '%', animationDelay: (i * 0.08) + 's' }} />
      ))}
    </div>
  );
}

// ── Empty state (prompt template) ─────────────────────────────────────────

function EmptyState({ section, onGen }: { section: Section; mode?: ViewMode; onGen: (id: string) => void }) {
  return (
    <div style={{
      border: '1px dashed var(--ai-line)', borderRadius: 'var(--r)', background: 'var(--ai-soft)',
      padding: '16px 18px', display: 'flex', gap: 14, alignItems: 'flex-start', animation: 'doc-fade-up 0.25s ease',
    }}>
      <span style={{
        width: 30, height: 30, borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--ai-line)',
        display: 'grid', placeItems: 'center', color: 'var(--ai-ink)', flexShrink: 0,
      }}><Sparkle size={16} /></span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="mono" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ai-ink)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 }}>Prompt template</div>
        <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55, fontStyle: 'italic' }}>{section.prompt}</div>
        <button className="doc-btn ai sm" onClick={() => onGen(section.id)} style={{ marginTop: 11 }}>
          <Sparkle size={14} />Generate this section
        </button>
      </div>
    </div>
  );
}

// ── Section toolbar (hover) ────────────────────────────────────────────────

function ToolBtn({ icon: Icon, label, onClick, tone }: { icon: React.FC<{ size?: number }>; label: string; onClick: () => void; tone?: 'ai' | 'danger' }) {
  const color = tone === 'ai' ? 'var(--ai-ink)' : tone === 'danger' ? 'oklch(0.55 0.16 25)' : 'var(--ink-2)';
  return (
    <button
      className="doc-icon-btn"
      onClick={onClick}
      title={label}
      style={{ color, width: 27, height: 27 }}
    >
      <Icon size={15} />
    </button>
  );
}

function SecToolbar({ section, mode, onGen, onVisualize, onDelete }: {
  section: Section; mode: ViewMode;
  onGen: () => void; onVisualize: () => void; onDelete: () => void;
}) {
  if (mode !== 'edit') return null;
  return (
    <div className="doc-sec-tools">
      <ToolBtn icon={Sparkle} label={section.status === 'empty' ? 'Generate' : 'Regenerate'} tone="ai" onClick={onGen} />
      <ToolBtn icon={Bulb} label="Recommend" onClick={() => {}} />
      <ToolBtn icon={Chart} label="Visualize" onClick={onVisualize} />
      <ToolBtn icon={Pencil} label="Edit" onClick={() => {}} />
      <div style={{ width: 1, background: 'var(--line)', margin: '3px 2px' }} />
      <ToolBtn icon={Trash} label="Delete" tone="danger" onClick={onDelete} />
    </div>
  );
}

// ── Media / diagram placeholder ────────────────────────────────────────────

function MediaBlock() {
  return (
    <div className="doc-media-ph" style={{ height: 168, margin: '4px 0 14px', flexDirection: 'column', gap: 6 }}>
      <Chart size={22} />
      <span className="mono" style={{ fontSize: 11 }}>driver-tree diagram</span>
    </div>
  );
}

// ── Avatar ─────────────────────────────────────────────────────────────────

function Avatar({ personId, size = 22 }: { personId: string; size?: number }) {
  const p = people[personId] ?? { initials: '?', hue: 258, isAI: false };
  return (
    <div title={p.name} style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'grid', placeItems: 'center', fontSize: size * 0.38, fontWeight: 700,
      fontFamily: 'var(--mono)', color: 'white', letterSpacing: '-0.02em',
      background: p.isAI ? 'var(--ai)' : `oklch(0.6 0.14 ${p.hue})`,
    }}>{p.initials}</div>
  );
}

// ── Section view ───────────────────────────────────────────────────────────

function SectionView({
  section, idx, mode, active, onGen, onDelete, onVisualize, hasViz, setRef, showNumbers,
}: {
  section: Section; idx: number; mode: ViewMode; active: boolean;
  onGen: (id: string) => void; onDelete: (id: string) => void; onVisualize: (id: string) => void;
  hasViz: boolean; setRef: (el: HTMLElement | null) => void; showNumbers: boolean;
}) {
  return (
    <section
      ref={setRef as React.Ref<HTMLElement>}
      className="doc-section-wrap"
      style={{
        position: 'relative', padding: '14px 0', scrollMarginTop: 80,
        borderRadius: 'var(--r)', transition: 'background 0.2s',
        background: active ? 'var(--blue-soft)' : 'transparent',
        boxShadow: active ? 'inset 0 0 0 1px var(--blue-line)' : 'none',
      }}
    >
      <div style={{ position: 'relative', padding: '0 6px' }}>
        {/* heading */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          {mode === 'edit' && (
            <span style={{ color: 'var(--faint)', cursor: 'grab', position: 'absolute', left: -16 }}>
              <Grip size={16} />
            </span>
          )}
          {showNumbers && (
            <span className="mono" style={{ fontSize: 11, color: 'var(--faint)', fontWeight: 600, width: 24 }}>
              {String(idx).padStart(2, '0')}
            </span>
          )}
          <h2 style={{ margin: 0, fontSize: 21, fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink)', flex: 1 }}>{section.title}</h2>
          {section.status === 'filled' && section.aiGenerated && (
            <span className="doc-chip" style={{ background: 'var(--ai-soft)', color: 'var(--ai-ink)', border: '1px solid var(--ai-line)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <Sparkle size={10} />AI draft
            </span>
          )}
          {section.hasFlag && (
            <span title="Has open recommendation" style={{ width: 7, height: 7, borderRadius: 4, background: 'var(--warn)', display: 'inline-block' }} />
          )}
          {section.author && <Avatar personId={section.author} size={22} />}
        </div>

        {/* body */}
        <div style={{ paddingLeft: showNumbers ? 34 : 0 }}>
          {section.status === 'generating' && <Skeleton />}
          {section.status === 'empty' && <EmptyState section={section} onGen={onGen} />}
          {section.status === 'filled' && section.blocks?.map((b, i) => <DocBlock key={i} block={b} />)}
          {hasViz && <MediaBlock />}

          {/* subsections */}
          {section.children?.map((c) => (
            <div key={c.id} style={{ marginTop: 16 }}>
              <h3 style={{ margin: '0 0 9px', fontSize: 16, fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 8 }}>
                {c.title}
                {c.aiGenerated && (
                  <span className="doc-chip" style={{ background: 'var(--ai-soft)', color: 'var(--ai-ink)', border: '1px solid var(--ai-line)' }}>AI draft</span>
                )}
              </h3>
              {c.status === 'generating' && <Skeleton />}
              {c.status === 'empty' && <EmptyState section={c} onGen={onGen} />}
              {c.status === 'filled' && c.blocks?.map((b, i) => <DocBlock key={i} block={b} />)}
            </div>
          ))}
        </div>

        <SecToolbar
          section={section} mode={mode}
          onGen={() => onGen(section.id)}
          onVisualize={() => onVisualize(section.id)}
          onDelete={() => onDelete(section.id)}
        />
      </div>
    </section>
  );
}

// ── Count helpers ──────────────────────────────────────────────────────────

function countFilled(secs: Section[]) {
  let n = 0;
  secs.forEach((s) => {
    if (s.children) s.children.forEach((c) => { if (c.status === 'filled') n++; });
    else if (s.status === 'filled') n++;
  });
  return n;
}
function countTotal(secs: Section[]) {
  let n = 0;
  secs.forEach((s) => { if (s.children) n += s.children.length; else n++; });
  return n;
}

// ── Editor ─────────────────────────────────────────────────────────────────

interface EditorProps {
  doc: Document;
  sections: Section[];
  mode: ViewMode;
  activeSectionId: string | null;
  onGen: (id: string) => void;
  onDelete: (id: string) => void;
  onVisualize: (id: string) => void;
  onAddSection: () => void;
  vizSet: Set<string>;
  registerRef: (id: string, el: HTMLElement | null) => void;
  docWidth?: number;
  showNumbers?: boolean;
}

export function DocEditor({
  doc, sections, mode, activeSectionId, onGen, onDelete, onVisualize,
  onAddSection, vizSet, registerRef, docWidth = 760, showNumbers = true,
}: EditorProps) {
  const filled = countFilled(sections);
  const total = countTotal(sections);

  return (
    <main style={{ overflowY: 'auto', background: 'var(--bg)', height: '100%' }}>
      <div style={{ maxWidth: docWidth, margin: '0 auto', padding: '40px clamp(22px, 4vw, 40px) 120px' }}>
        {/* doc header */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, color: 'var(--muted)', fontSize: 12.5, flexWrap: 'wrap' }}>
            <span className="doc-chip" style={{ background: 'var(--blue-soft)', color: 'var(--blue-ink)' }}>{doc.type}</span>
            <span className="mono" style={{ fontSize: 11 }}>{doc.code}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Clock size={13} /> Updated {doc.updated}
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 7 }}>
              <span className="mono" style={{ fontSize: 11 }}>{filled}/{total} sections</span>
              <div style={{ width: 60, height: 5, borderRadius: 3, background: 'var(--bg-2)', overflow: 'hidden' }}>
                <div style={{ width: (filled / total * 100) + '%', height: '100%', background: 'var(--good)', borderRadius: 3, transition: 'width 0.4s' }} />
              </div>
            </span>
          </div>
          <h1 style={{ margin: 0, fontSize: 34, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1, color: 'var(--ink)' }}>{doc.title}</h1>
        </div>
        <div style={{ height: 1, background: 'var(--line)', margin: '0 0 4px' }} />

        {sections.map((s, i) => (
          <SectionView
            key={s.id} section={s} idx={i + 1} mode={mode}
            active={activeSectionId === s.id}
            onGen={onGen} onDelete={onDelete} onVisualize={onVisualize}
            hasViz={vizSet.has(s.id)} showNumbers={showNumbers}
            setRef={(el) => registerRef(s.id, el)}
          />
        ))}

        {mode === 'edit' && (
          <button
            onClick={onAddSection}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              width: '100%', height: 44, marginTop: 14, border: '1px dashed var(--line)', borderRadius: 'var(--r)',
              background: 'transparent', color: 'var(--muted)', fontWeight: 600, fontSize: 13.5,
              transition: 'all 0.14s', cursor: 'pointer', fontFamily: 'var(--sans)',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-2)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--muted)'; }}
          >
            <Plus size={16} />Add section
          </button>
        )}
      </div>
    </main>
  );
}
